/**
 * Member Enrichment API Service
 * Handles all member AI enrichment API calls
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/lib/utils';

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Authorization': `Bearer ${token}`,
    'X-CBO-ID': cboId,
  };
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface EnrichmentSummary {
  member_id: number;
  cbo_id: number;
  overall_score: number;
  data_quality_score: number;
  contact_validity_score: number;
  sdoh_risk_score: number;
  clinical_risk_score: number;
  engagement_score: number;
  financial_risk_score: number;
  eligibility_score: number;
  enrichment_completeness: number;
  last_enriched_at: string | null;
  next_enrichment_due: string | null;
  total_modules_run: number;
  total_issues_found: number;
  eligible_programs: any[];
  risk_flags: any[];
  sdoh_needs: any[];
  care_gaps: any[];
}

export interface EnrichmentResult {
  id: number;
  module_key: string;
  status: 'completed' | 'failed' | 'skipped' | 'pending';
  confidence_score: number | null;
  fields_enriched: number;
  fields_validated: number;
  fields_corrected: number;
  issues_found: any[];
  suggestions: any[];
  external_source: string | null;
  cost_usd: number;
  duration_ms: number | null;
  error_message: string | null;
  run_id: string | null;
  created_at: string;
  scores: Record<string, number> | null;
  metadata: Record<string, any> | null;
}

export interface EnrichmentModule {
  module_key: string;
  display_name: string;
  description: string | null;
  phase: number;
  category: string;
  requires_consent: boolean;
  requires_credentials: boolean;
  cost_per_run: number;
  is_active: boolean;
  org_enabled: boolean | null;
  auto_trigger: boolean | null;
  schedule: string | null;
}

export interface ModuleConfigUpdate {
  is_enabled?: boolean;
  auto_trigger_on_create?: boolean;
  schedule_frequency?: string;
  credentials_json?: Record<string, any>;
}

export interface EnrichmentDashboard {
  total_members_enriched: number;
  well_enriched: number;
  partially_enriched: number;
  needs_enrichment: number;
  never_enriched: number;
  avg_overall_score: number;
  avg_data_quality: number;
  avg_sdoh_risk: number;
  avg_engagement: number;
  high_sdoh_risk_count: number;
  total_issues_found: number;
  recent_activity: {
    module: string;
    runs_7d: number;
    avg_confidence: number;
    cost_7d: number;
  }[];
}

export interface EligibilityRule {
  id: number;
  cbo_id: number;
  program_id: number | null;
  rule_name: string;
  field_name: string;
  operator: string;
  value_threshold: string | null;
  value_list: any[] | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface EligibilityRuleCreate {
  program_id?: number | null;
  rule_name: string;
  field_name: string;
  operator: string;
  value_threshold?: string | null;
  value_list?: any[] | null;
  description?: string | null;
  is_active?: boolean;
}

export interface PendingChange {
  id: number;
  member_id: number;
  module_key: string;
  module_label: string;
  field_name: string;
  field_label: string;
  old_value: string | null;
  new_value: string;
  reason: string | null;
  confidence: number;
  is_custom_field: boolean;
  custom_field_key: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'applied' | 'expired';
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  applied_at: string | null;
  created_at: string;
  expires_at: string | null;
  run_id: string | null;
  reviewer_name: string | null;
}

export interface RetentionScore {
  engagement_score: number;
  risk_level: string;
  trend_direction: string | null;
  days_since_last_contact: number | null;
  churn_probability: number | null;
  component_scores: Record<string, number> | null;
  calculated_at: string | null;
}

export interface RetentionDashboardSummary {
  total_members: number;
  at_risk_count: number;
  critical_count: number;
  avg_engagement: number;
  churn_predictions_count: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const API = () => `${getApiBaseUrl()}/api/enrichment`;
const RETENTION_API = () => `${getApiBaseUrl()}/api/retention`;

export const memberEnrichmentAPI = {
  // Trigger enrichment for a single member
  async enrichMember(memberId: number, modules?: string[]): Promise<{ status: string; message: string }> {
    const res = await axios.post(`${API()}/enrich`, { member_id: memberId, modules }, { headers: getHeaders() });
    return res.data;
  },

  // Trigger batch enrichment
  async enrichBatch(memberIds?: number[], limit = 50, staleHours = 24): Promise<{ status: string; message: string }> {
    const res = await axios.post(`${API()}/enrich-batch`, { member_ids: memberIds, limit, stale_hours: staleHours }, { headers: getHeaders() });
    return res.data;
  },

  // Get enrichment summary for a member
  async getSummary(memberId: number): Promise<EnrichmentSummary | null> {
    try {
      const res = await axios.get(`${API()}/summary/${memberId}`, { headers: getHeaders() });
      return res.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  // Get detailed enrichment results for a member
  async getResults(memberId: number, moduleKey?: string, limit = 20): Promise<{ member_id: number; results: EnrichmentResult[] }> {
    const params: any = { limit };
    if (moduleKey) params.module_key = moduleKey;
    const res = await axios.get(`${API()}/results/${memberId}`, { headers: getHeaders(), params });
    return res.data;
  },

  // List all enrichment modules with CBO config
  async getModules(): Promise<{ modules: EnrichmentModule[] }> {
    const res = await axios.get(`${API()}/modules`, { headers: getHeaders() });
    return res.data;
  },

  // Update module configuration for this CBO
  async updateModuleConfig(moduleKey: string, config: ModuleConfigUpdate): Promise<any> {
    const res = await axios.put(`${API()}/modules/${moduleKey}/config`, config, { headers: getHeaders() });
    return res.data;
  },

  // Get CBO-level enrichment dashboard stats
  async getDashboard(): Promise<EnrichmentDashboard> {
    const res = await axios.get(`${API()}/dashboard`, { headers: getHeaders() });
    return res.data;
  },

  // Eligibility Rules CRUD
  async getEligibilityRules(programId?: number): Promise<{ rules: EligibilityRule[] }> {
    const params: any = {};
    if (programId) params.program_id = programId;
    const res = await axios.get(`${API()}/eligibility-rules`, { headers: getHeaders(), params });
    return res.data;
  },

  async createEligibilityRule(rule: EligibilityRuleCreate): Promise<EligibilityRule> {
    const res = await axios.post(`${API()}/eligibility-rules`, rule, { headers: getHeaders() });
    return res.data;
  },

  async updateEligibilityRule(ruleId: number, rule: Partial<EligibilityRuleCreate>): Promise<EligibilityRule> {
    const res = await axios.put(`${API()}/eligibility-rules/${ruleId}`, rule, { headers: getHeaders() });
    return res.data;
  },

  async deleteEligibilityRule(ruleId: number): Promise<void> {
    await axios.delete(`${API()}/eligibility-rules/${ruleId}`, { headers: getHeaders() });
  },

  // Pending changes — review workflow
  async getPendingChanges(memberId: number, status?: string): Promise<{ member_id: number; changes: PendingChange[] }> {
    const params: any = {};
    if (status) params.status_filter = status;
    const res = await axios.get(`${API()}/pending-changes/${memberId}`, { headers: getHeaders(), params });
    return res.data;
  },

  async approveChange(changeId: number, reviewNotes?: string): Promise<{ status: string; change_id: number }> {
    const res = await axios.post(`${API()}/changes/approve`, { change_id: changeId, review_notes: reviewNotes }, { headers: getHeaders() });
    return res.data;
  },

  async rejectChange(changeId: number, reviewNotes?: string): Promise<{ status: string; change_id: number }> {
    const res = await axios.post(`${API()}/changes/reject`, { change_id: changeId, review_notes: reviewNotes }, { headers: getHeaders() });
    return res.data;
  },

  async bulkApproveChanges(changeIds: number[], reviewNotes?: string): Promise<{ applied: number[]; failed: number[] }> {
    const res = await axios.post(`${API()}/changes/bulk-approve`, { change_ids: changeIds, review_notes: reviewNotes }, { headers: getHeaders() });
    return res.data;
  },

  async bulkRejectChanges(changeIds: number[], reviewNotes?: string): Promise<{ rejected: number[] }> {
    const res = await axios.post(`${API()}/changes/bulk-reject`, { change_ids: changeIds, review_notes: reviewNotes }, { headers: getHeaders() });
    return res.data;
  },

  // Retention system integration — fetch engagement/churn data
  async getRetentionScore(memberId: number): Promise<RetentionScore | null> {
    try {
      const cboId = localStorage.getItem('active_cbo_id');
      const res = await axios.get(`${RETENTION_API()}/scores/${memberId}`, {
        headers: getHeaders(),
        params: { cbo_id: cboId },
      });
      return res.data;
    } catch {
      return null;
    }
  },

  async getRetentionDashboard(cboId: number): Promise<RetentionDashboardSummary | null> {
    try {
      const res = await axios.get(`${RETENTION_API()}/dashboard/${cboId}`, { headers: getHeaders() });
      return {
        total_members: res.data.total_members || 0,
        at_risk_count: res.data.at_risk_count || 0,
        critical_count: res.data.critical_count || 0,
        avg_engagement: res.data.avg_engagement_score || 0,
        churn_predictions_count: res.data.churn_predictions_count || 0,
      };
    } catch {
      return null;
    }
  },
};
